import { PermissionGuard } from '@/components/PermissionGuard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDeleteInvitation } from '@/hooks/useInvitationActions';
import { invitationStatusMapping } from '@/lib/invitationStatusMapping';
import { roleMapping } from '@/lib/roleMapping';
import { Loader2, X } from 'lucide-react';
import { formatDateShort } from '@/lib/format';

interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
}

interface InvitationsSectionProps {
  pendingInvitations: PendingInvitation[];
  isLoading: boolean;
}

export function InvitationsSection({
  pendingInvitations,
  isLoading,
}: InvitationsSectionProps) {
  const { mutate: deleteInvitation, isPending: isDeletingInvitation } =
    useDeleteInvitation();

  if (isLoading || pendingInvitations.length === 0) return null;

  return (
    <section>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-slate-900">Invitations en attente</h2>
      </div>

      <div className="space-y-2">
        {pendingInvitations.map((invitation) => (
          <div
            key={invitation.id}
            className="bg-white rounded-xl px-5 py-4 hover:shadow-md transition-all flex items-center gap-4"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-900 truncate">{invitation.email}</p>
              <p className="text-xs text-slate-500">
                Invité le {formatDateShort(invitation.createdAt)}
              </p>
            </div>

            <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0">
              {roleMapping[invitation.role]}
            </Badge>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className="h-2.5 w-2.5 rounded-full bg-amber-500 shrink-0" />
              </TooltipTrigger>
              <TooltipContent>
                {invitationStatusMapping[invitation.status]}
              </TooltipContent>
            </Tooltip>

            <PermissionGuard permission="delete">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50 shrink-0"
                onClick={() =>
                  deleteInvitation({ invitationId: invitation.id })
                }
                disabled={isDeletingInvitation}
              >
                {isDeletingInvitation ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <X className="h-3.5 w-3.5 mr-1" />
                )}
                Annuler
              </Button>
            </PermissionGuard>
          </div>
        ))}
      </div>
    </section>
  );
}
